// Public volunteer UI: browsing, applying for roles, check-in, and the audition reel.
//
// One bundle serves every /volunteer page; each marks itself with data-vol-public="..."
// and only that page's controller runs.

'use strict';

const axios = require('axios');
const upload = require('./common/upload');
const volSocket = require('./volunteer/socket');
const notifyUI = require('./volunteer/notify-ui');

const $ = (sel, root) => (root || document).querySelector(sel);
const $$ = (sel, root) => Array.prototype.slice.call((root || document).querySelectorAll(sel));

const errText = (err) => {
	const d = err && err.response && err.response.data;
	return (d && d.message) || (err && err.message) || 'Something went wrong';
};

// Chat bodies and display names are user-supplied and rendered with innerHTML, so they
// must be escaped on the way in.
const esc = (s) => String(s == null ? '' : s)
	.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
	.replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Role write-ups are admin-authored plain text. Escape it, then honour paragraph breaks —
// a three-paragraph job description collapsed into one block is unreadable, which defeats
// the point of offering it.
const asParagraphs = (text) => String(text == null ? '' : text)
	.split(/\n\s*\n/)
	.map((p) => p.trim())
	.filter(Boolean)
	.map((p) => '<p>' + esc(p).replace(/\n/g, '<br>') + '</p>')
	.join('');

const disable = (btn, label) => {
	if (!btn) return () => {};
	const original = btn.innerHTML;
	btn.disabled = true;
	if (label) btn.innerHTML = label;
	return () => { btn.disabled = false; btn.innerHTML = original; };
};

// ─── index: "venues near me" ─────────────────────────────────────────────────

function indexPage() {
	const btn = $('#vol-near-me');
	if (!btn) return;

	const status = $('#vol-geo-status');

	btn.addEventListener('click', () => {
		if (!navigator.geolocation) {
			status.textContent = 'Your browser cannot share a location.';
			return;
		}

		const restore = disable(btn, 'Locating&hellip;');
		status.textContent = '';

		navigator.geolocation.getCurrentPosition(
			(pos) => {
				window.location.href = '/volunteer?lat=' + pos.coords.latitude + '&lon=' + pos.coords.longitude;
			},
			() => {
				restore();
				// Denying location is a normal choice, not an error worth alarming about.
				status.textContent = 'No problem — showing all venues instead.';
			},
			{timeout: 8000, maximumAge: 300000}
		);
	});
}

// ─── showing: apply for a role ───────────────────────────────────────────────

// "Character Information" / "Job Details" on a role card. Both open the same dialog; only
// which slice of the role's copy it shows differs.
function roleInfoPanels() {
	const modal = $('#role-info-modal');
	if (!modal) return;

	const info = window.__volRoleInfo || {};
	const titleEl = $('#role-info-title');
	const subEl = $('#role-info-sub');
	const bodyEl = $('#role-info-body');

	$$('[data-role-info]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const role = info[btn.getAttribute('data-role-info')];
			if (!role) return;

			const isCharacter = btn.getAttribute('data-info-kind') === 'character';

			titleEl.textContent = isCharacter ? (role.characterName || role.roleName) : role.roleName;

			// Underneath: the role name, but only when the character is credited differently
			// ("JESUS / JESUS" is noise), plus the scenes the part appears in — someone
			// reading the write-up is deciding whether to take it, and that is the other half
			// of the answer.
			const sub = [];
			if (isCharacter && role.characterName && role.characterName !== role.roleName) {
				sub.push(role.roleName);
			}
			if (isCharacter && role.sceneLabel) sub.push(role.sceneLabel);

			subEl.textContent = sub.join(' · ');
			subEl.style.display = subEl.textContent ? '' : 'none';

			bodyEl.innerHTML = asParagraphs(
				isCharacter ? role.characterDescription : role.jobDescription);

			window.jQuery(modal).modal('show');
		});
	});
}

// "Sort by" on the speaking-roles group. Cards are moved in place rather than re-rendered —
// all the state (fill bar, blocked reasons, apply button) already lives in the DOM.
function roleSort() {
	const select = $('#vol-speaking-sort');
	const container = $('#vol-speaking-roles');
	if (!select || !container) return;

	const READERS = {
		name: (card) => card.getAttribute('data-sort-name') || '',
		scenes: (card) => -Number(card.getAttribute('data-sort-scenes') || 0),
		lines: (card) => -Number(card.getAttribute('data-sort-lines') || 0),
	};

	select.addEventListener('change', () => {
		const read = READERS[select.value] || READERS.name;
		const cards = $$('[data-sort-name]', container);

		cards.sort((a, b) => {
			const av = read(a);
			const bv = read(b);
			if (av < bv) return -1;
			if (av > bv) return 1;
			return 0;
		});

		// Re-appending in the new order moves each node rather than cloning it.
		cards.forEach((card) => container.appendChild(card));
	});
}

// Venue password gate (app/utils/volunteer/gate.js). The cookie set by a successful submit is
// authoritative; localStorage is purely a same-device convenience so a returning visitor whose
// cookie lapsed doesn't have to retype the password — best-effort and silent on failure, same
// idiom as volunteer/notify-ui.js's sound preference.
const GATE_KEY_PREFIX = 'vol-gate-pw:';

function bindVenueGate(root) {
	const wrap = $('#vol-gate', root);
	if (!wrap) return;   // gate is open (or this venue has none) — nothing to do

	const venueSlug = wrap.getAttribute('data-venue-slug');
	const form = $('#vol-gate-form', wrap);
	const errorEl = $('.vol-gate-error', wrap);

	const submit = (password) => axios.post('/volunteer/' + venueSlug + '/gate', {password});

	let remembered = null;
	try {
		remembered = window.localStorage.getItem(GATE_KEY_PREFIX + venueSlug);
	} catch (e) { /* private mode, blocked storage, etc. — fall through to the manual form */ }

	// Silent auto-unlock: if this device already knows the password, don't make them retype it.
	if (remembered) {
		submit(remembered).then(() => window.location.reload()).catch(() => {});
	}

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		const password = form.password.value;
		const restore = disable($('button[type="submit"]', form), 'Checking&hellip;');

		submit(password).then(() => {
			try {
				window.localStorage.setItem(GATE_KEY_PREFIX + venueSlug, password);
			} catch (e) { /* best-effort only */ }
			window.location.reload();
		}).catch((err) => {
			restore();
			errorEl.textContent = errText(err);
			errorEl.hidden = false;
		});
	});
}

function showingPage(root) {
	const showingId = root.getAttribute('data-showing-id');
	const canOnBehalf = root.getAttribute('data-can-on-behalf') === 'true';

	// Runs even behind a shut gate — the gate panel is all there is to wire up in that case.
	bindVenueGate(root);

	// Long-form copy always renders, signed in or not — reading about a part is how someone
	// decides whether to sign in at all. Wired before the apply modal's early return.
	roleInfoPanels();
	roleSort();

	const modal = $('#apply-modal');
	if (!modal) return;

	const reel = window.__volReel || [];
	const roleInfo = window.__volRoleInfo || {};
	const eligibility = window.__volEligibility || {maxRoles: 1, byParticipant: {}};
	let current = null;   // {roleId, roleName, requiresAudition, open}

	const videoGroup = $('#apply-video-group');
	const videoList = $('#apply-video-list');
	const videoHelp = $('#apply-video-help');
	const standbyNote = $('#apply-standby-note');
	const errorBox = $('#apply-error');
	const participantSel = $('#apply-participant');
	const briefBox = $('#apply-audition-details');
	const briefText = $('#apply-audition-details-text');

	// Which video the applicant has picked, or '' when they have none on file.
	const chosenVideo = () => {
		const picked = $('input[name="apply-video"]:checked', videoList);
		return picked ? picked.value : '';
	};

	// The reel is per-person, so the video list has to follow the participant dropdown.
	const syncVideos = () => {
		if (!current || !current.requiresAudition) {
			videoGroup.style.display = 'none';
			return;
		}

		videoGroup.style.display = '';
		const owner = participantSel.value;
		const mine = reel.filter((v) => String(v.owner) === String(owner));

		if (!mine.length) {
			videoList.innerHTML = '<p class="vol-video-none">— no videos on file —</p>';
			videoHelp.innerHTML =
				'This role needs an audition video. <a href="/volunteer/reel">Upload one to the reel</a>, then come back.';
			return;
		}

		// Labels are whatever the participant typed, and this goes in with innerHTML.
		videoList.innerHTML = mine.map((v, i) => {
			const thumb = v.hasPoster
				? '<img class="vol-video-thumb" src="/volunteer/reel/' + v._id + '/poster" alt="" loading="lazy">'
				: '<span class="vol-video-thumb vol-video-blank"></span>';

			const secs = Math.round(v.durationSeconds || 0);
			const length = secs
				? '<span class="vol-video-dur">' + Math.floor(secs / 60) + ':' +
					(secs % 60 < 10 ? '0' : '') + (secs % 60) + '</span>'
				: '';

			return '<label class="vol-video-option">' +
				'<input type="radio" name="apply-video" value="' + esc(v._id) + '"' +
					(i === 0 ? ' checked' : '') + '>' +
				thumb +
				'<span class="vol-video-name">' + esc(v.label) + '</span>' +
				length +
				'</label>';
		}).join('');

		videoHelp.textContent = 'A director will watch this before deciding.';
	};

	/**
	 * Why a given person cannot take the currently-selected role, or '' when they can.
	 *
	 * Eligibility is per person, not per viewer: a parent already holding two parts may be
	 * here precisely to put a child in a third, and a child already cast as Adam cannot also
	 * be Eve. Anyone absent from the blob holds nothing here and is unconstrained.
	 */
	const reasonFor = (participantId) => {
		if (!current) return '';
		const entry = eligibility.byParticipant[String(participantId)];
		return (entry && entry.blocked && entry.blocked[current.roleId]) || '';
	};

	const blockedReason = () => reasonFor(participantSel.value);

	/**
	 * Relabel and re-disable the who-is-volunteering list for the chosen role.
	 *
	 * `data-gated` marks an option the SERVER disabled because the on-behalf gate is shut —
	 * that is not per-role and must survive this pass, so it is re-applied rather than
	 * recomputed. Everything else is restored from data-base-label first, or the reasons
	 * would accumulate on the label each time the dialog opens.
	 */
	const syncParticipants = () => {
		const options = Array.prototype.slice.call(participantSel.options);

		options.forEach((opt) => {
			const base = opt.getAttribute('data-base-label') || opt.textContent.trim();
			const gated = opt.hasAttribute('data-gated');
			const reason = gated ? '' : reasonFor(opt.value);

			opt.disabled = gated || !!reason;
			opt.textContent = gated
				? base + ' — take a role yourself first'
				: base + (reason ? ' — ' + reason : '');
		});

		// Land on somebody who can actually be signed up, rather than opening on a dead
		// selection the viewer has to work out how to escape.
		const selected = options[participantSel.selectedIndex];
		if (!selected || selected.disabled) {
			const free = options.filter((o) => !o.disabled)[0];
			if (free) participantSel.value = free.value;
		}
	};

	// Runs whenever either half of the (person, role) pair changes.
	const syncEligibility = () => {
		const reason = blockedReason();
		const who = participantSel.options[participantSel.selectedIndex];
		const name = (who && who.getAttribute('data-base-label')) || '';
		const isSelf = participantSel.selectedIndex === 0;

		// Name whose problem it is: the same sentence is used for the viewer and for anyone
		// they manage, and an unattributed refusal beside a person picker is ambiguous.
		errorBox.textContent = reason && !isSelf ? name + ': ' + reason : reason;
		errorBox.style.display = reason ? '' : 'none';
		$('#apply-confirm').disabled = !!reason;
	};

	participantSel.addEventListener('change', () => {
		syncVideos();
		syncEligibility();
	});

	$$('.vol-apply').forEach((btn) => {
		btn.addEventListener('click', () => {
			current = {
				roleId: btn.getAttribute('data-role-id'),
				roleName: btn.getAttribute('data-role-name'),
				requiresAudition: btn.getAttribute('data-requires-audition') === 'true',
				open: parseInt(btn.getAttribute('data-open'), 10) || 0,
			};

			$('#apply-role-name').textContent = current.roleName;
			errorBox.style.display = 'none';
			$('#apply-confirm').disabled = false;

			// What this particular director wants to see. Only meaningful on an
			// audition-gated role — nothing is submitted otherwise.
			const brief = roleInfo[current.roleId];
			const auditionDetails =
				(current.requiresAudition && brief && brief.auditionDetails) || '';

			briefText.innerHTML = asParagraphs(auditionDetails);
			briefBox.style.display = auditionDetails ? '' : 'none';

			// Only warn about standby when the role is genuinely full and not audition-gated
			// (an audition is reviewed regardless of how full the role currently is).
			standbyNote.style.display = (!current.requiresAudition && current.open === 0) ? '' : 'none';

			// No managed accounts to choose between, or not yet allowed to use them.
			$('#apply-who-group').style.display =
				participantSel.options.length > 1 ? '' : 'none';

			// Order matters: pick the person first, since the video list and the eligibility
			// message both describe whoever ends up selected.
			syncParticipants();
			syncVideos();
			syncEligibility();
			window.jQuery(modal).modal('show');
		});
	});

	$('#apply-confirm').addEventListener('click', async () => {
		if (!current) return;

		// Belt and braces: the option is disabled and Confirm is greyed, but a stale page or a
		// keyboard selection could still land here. The server is the real authority.
		if (blockedReason()) {
			syncEligibility();
			return;
		}

		const videoId = chosenVideo();

		if (current.requiresAudition && !videoId) {
			errorBox.textContent = 'Choose an audition video first, or upload one to your reel.';
			errorBox.style.display = '';
			return;
		}

		const restore = disable($('#apply-confirm'), 'Submitting&hellip;');
		errorBox.style.display = 'none';

		try {
			const res = await axios.post('/volunteer/showing/' + showingId + '/apply', {
				roleId: current.roleId,
				participantId: participantSel.value,
				auditionVideoId: current.requiresAudition ? videoId : undefined,
			});

			window.jQuery(modal).modal('hide');
			// Reload so every role card reflects the new counts, not just the one clicked.
			window.location.reload();
			void res;
		} catch (err) {
			restore();
			errorBox.textContent = errText(err);
			errorBox.style.display = '';
		}
	});

	void canOnBehalf;

	// ── check in, and cancel, without leaving this page ──
	// This URL is the one people have from their signup email, so the day-of actions have
	// to be here and not only on /volunteer/me.
	bindParticipantActions();

	// ── live: chat, and the on-deck banner ──
	// One socket serves both. The chat panel is only rendered for someone holding a place
	// here, but on-deck alerts must reach anyone the server will subscribe — so the
	// subscription is no longer conditional on the panel existing.
	if ($('#chat-panel') || $('#vol-notifications')) {
		initLive(showingId);
	}
}

// ─── live updates: notifications, and chat where it is rendered ──────────────

// One banner per page, however many showings are subscribed. /volunteer/me can be live for
// several at once, and createBanner attaches a delegated listener to the container — binding
// it per showing would fire one dismiss per subscription off a single click.
let pageBanner = null;

function getBanner(socket) {
	if (pageBanner) return pageBanner;

	pageBanner = notifyUI.createBanner($('#vol-notifications'), (id) =>
		socket.emit('notification:ack', {notificationId: id}, () => {}));

	notifyUI.bindSoundToggle($('#vol-sound-toggle'));
	return pageBanner;
}

function initLive(showingId) {
	const socket = volSocket.connect();
	const banner = getBanner(socket);

	const chat = $('#chat-panel') ? initChat(showingId, socket) : null;

	volSocket.subscribe(showingId, {
		onReady: (data) => {
			if (chat) chat.load(data.messages || []);

			// Replayed quietly: they may well have seen these already, and a page reload
			// should not set every phone in the green room buzzing again.
			(data.notifications || []).slice().reverse()
				.forEach((n) => banner.show(n, false));
		},
		onError: (err) => {
			// Losing your place (cancelled, or removed by an admin) also ends chat access.
			if (chat) chat.refuse(err);
		},
	});

	// Registered once for the socket, not once per showing: the namespace is shared, so a
	// second subscription would show every alert twice.
	if (!socket.__volNotifyBound) {
		socket.__volNotifyBound = true;

		socket.on('notification:new', (n) => banner.show(n, true));

		// An admin moving you shows up here immediately, so the page doesn't go stale
		// underneath someone standing in the venue.
		socket.on('assignment:mine', ({participant}) => {
			if (String(participant) === String(window.user && window.user.id)) window.location.reload();
		});
	}
}

// ─── shared participant actions (showing page and /volunteer/me) ─────────────

function bindParticipantActions() {
	$$('.vol-checkin').forEach((btn) => {
		btn.addEventListener('click', async () => {
			const restore = disable(btn, 'Checking in…');
			try {
				await axios.post('/volunteer/assignment/' + btn.getAttribute('data-assignment-id') + '/checkin');
				window.location.reload();
			} catch (err) {
				restore();
				window.alert(errText(err));
			}
		});
	});

	$$('.vol-cancel').forEach((btn) => {
		btn.addEventListener('click', async () => {
			const roleName = btn.getAttribute('data-role-name');
			if (!window.confirm(
				'Cancel your place as ' + roleName + '?\n\n' +
				'Your spot will be offered to someone else.'
			)) return;

			const restore = disable(btn, 'Canceling…');
			try {
				await axios.post('/volunteer/assignment/' + btn.getAttribute('data-assignment-id') + '/cancel');
				window.location.reload();
			} catch (err) {
				restore();
				window.alert(errText(err));
			}
		});
	});
}

// ─── chat ────────────────────────────────────────────────────────────────────

function initChat(showingId, socket) {
	const log = $('#chat-log');
	const empty = $('#chat-empty');
	const form = $('#chat-form');
	const input = $('#chat-input');

	const setConn = volSocket.bindConnectionPill($('#chat-conn'), 'vol-chat-conn');

	const nameOf = (u) => u
		? (u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Someone')
		: 'Someone';

	const append = (msg, isNew) => {
		if (empty) empty.style.display = 'none';

		const el = document.createElement('div');
		el.className = 'vol-msg' + (msg.isAdminMessage ? ' vol-msg-admin' : '');
		el.setAttribute('data-message-id', msg._id);

		const when = new Date(msg.createdAt);
		el.innerHTML =
			'<div class="vol-msg-meta">' +
				'<strong>' + esc(nameOf(msg.user)) + '</strong>' +
				(msg.isAdminMessage ? ' <span class="badge badge-primary">organizer</span>' : '') +
				'<span class="vol-msg-when">' + esc(when.toLocaleString()) + '</span>' +
			'</div>' +
			'<div class="vol-msg-body">' + esc(msg.body) + '</div>';

		log.appendChild(el);
		if (isNew) log.scrollTop = log.scrollHeight;
	};

	socket.on('chat:message', (msg) => append(msg, true));

	socket.on('chat:delete', ({messageId}) => {
		const el = log.querySelector('[data-message-id="' + messageId + '"]');
		if (el) el.remove();
	});

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		const body = input.value.trim();
		if (!body) return;

		input.value = '';
		socket.emit('chat:send', {showingId, body}, (err) => {
			if (err) {
				input.value = body;   // hand the text back rather than losing it
				window.alert(err.message || 'That message could not be sent.');
			}
		});
	});

	return {
		// Called on every subscribe, including after a reconnect, so it must replace the log
		// rather than append to it — otherwise a flaky connection duplicates the history.
		load(messages) {
			log.innerHTML = '';
			if (empty) empty.style.display = messages.length ? 'none' : '';
			messages.forEach((m) => append(m, false));
			log.scrollTop = log.scrollHeight;
		},

		refuse(err) {
			setConn('error', (err && err.message) || 'unavailable');
			form.style.display = 'none';
		},
	};
}

// ─── me: cancel and check in ─────────────────────────────────────────────────

function mePage(root) {
	// Identical controls to the ones on a showing page — same markup, same behaviour.
	bindParticipantActions();

	bindNotificationPrefs();

	// This page had no socket at all before on-deck alerts existed, and it is the page a
	// volunteer is most likely to have open on the day — it lists everything they are down
	// for. Subscribe to whichever of those showings is happening now.
	//
	// The server only marks a showing "live" here if it is inside its check-in window, so a
	// booking three weeks out does not hold a subscription open for nothing.
	const live = (root && root.getAttribute('data-live-showings') || '')
		.split(',').map((s) => s.trim()).filter(Boolean);

	if (live.length) live.forEach((showingId) => initLive(showingId));
}

// ─── me: email preferences ───────────────────────────────────────────────────

// Preferences are opt-OUT, so what we post is the list of topics whose boxes are UNCHECKED.
function bindNotificationPrefs() {
	const panel = $('#vol-prefs');
	if (!panel) return;

	const master = $('#vol-prefs-enabled', panel);
	const topics = $('#vol-prefs-topics', panel);
	const state = $('.vol-prefs-state', panel);
	const status = $('#vol-prefs-status', panel);

	if (!master || !topics) return;

	let timer = null;
	let inFlight = false;

	const syncDisabled = () => {
		const off = !master.checked;
		topics.classList.toggle('vol-prefs-muted', off);
		$$('.vol-prefs-topic-input', topics).forEach((box) => { box.disabled = off; });
		if (state) state.textContent = off ? 'Off' : 'On';
	};

	const save = async () => {
		if (inFlight) return;
		inFlight = true;
		if (status) status.textContent = 'Saving…';

		// Unchecked means unsubscribed. Read every box regardless of the master switch, so
		// turning notifications back on restores the individual choices rather than
		// silently resubscribing someone to everything.
		const unsubscribed = $$('.vol-prefs-topic-input', topics)
			.filter((box) => !box.checked)
			.map((box) => box.value);

		try {
			await axios.post('/volunteer/me/notifications', {
				enabled: master.checked,
				unsubscribed: unsubscribed,
			});
			if (status) status.textContent = 'Saved';
		} catch (err) {
			if (status) status.textContent = errText(err);
		} finally {
			inFlight = false;
		}
	};

	// One delegated listener on the panel, not one per checkbox — the lesson from the
	// board's drag handlers. Debounced so ticking several boxes is a single request.
	panel.addEventListener('change', (e) => {
		if (!e.target || e.target.type !== 'checkbox') return;

		syncDisabled();

		if (status) status.textContent = '';
		if (timer) clearTimeout(timer);
		timer = setTimeout(save, 600);
	});

	syncDisabled();
}

// ─── reel: record, upload and delete ─────────────────────────────────────────

const POSTER_QUALITY = 0.72;
// A decode that never finishes must not hold the upload hostage.
const POSTER_TIMEOUT_MS = 10000;

// MediaRecorder's WebM output carries no duration in its container, so `video.duration` reads
// Infinity until the element has been forced to scan to the end. Both the duration we store
// and the halfway seek for the poster need a real number.
const resolveDuration = (el) => new Promise((resolve) => {
	if (Number.isFinite(el.duration) && el.duration > 0) return resolve(el.duration);

	el.addEventListener('timeupdate', () => {
		el.currentTime = 0;
		resolve(Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null);
	}, {once: true});

	// Seeking far past the end is what makes the browser scan through and fill in duration.
	el.currentTime = 1e101;
});

// One off-DOM <video> does both jobs: read the real duration, then grab a frame from the
// middle of the clip. Without this every card on the reel and every row in the admin audition
// queue renders as an identical black rectangle.
//
// Anything that goes wrong — a codec the browser cannot decode, a stalled decode — resolves
// to nulls rather than throwing: a missing poster is a cosmetic loss, a failed upload is not.
const captureVideoPoster = (file, posterWidth) => new Promise((resolve) => {
	let settled = false;
	let timer = null;
	let el = null;
	let src = null;

	const finish = (duration, poster) => {
		if (settled) return;
		settled = true;
		clearTimeout(timer);
		if (el) {
			el.removeAttribute('src');
			el.load();
		}
		if (src) URL.revokeObjectURL(src);
		resolve({duration: duration, poster: poster});
	};

	timer = setTimeout(() => finish(null, null), POSTER_TIMEOUT_MS);

	try {
		el = document.createElement('video');
		el.preload = 'metadata';
		el.muted = true;
		el.playsInline = true;
		el.onerror = () => finish(null, null);

		el.onloadedmetadata = async () => {
			const duration = await resolveDuration(el);

			// Halfway through is far likelier to show the performer mid-piece than frame zero,
			// which on a phone recording is usually a hand reaching back for the button.
			const target = duration
				? Math.min(duration * 0.5, Math.max(duration - 0.1, 0.1))
				: 1;

			el.addEventListener('seeked', () => {
				let poster = null;

				try {
					const w = el.videoWidth;
					const h = el.videoHeight;

					if (w && h) {
						const scale = Math.min(1, posterWidth / w);
						const canvas = document.createElement('canvas');
						canvas.width = Math.round(w * scale);
						canvas.height = Math.round(h * scale);
						canvas.getContext('2d').drawImage(el, 0, 0, canvas.width, canvas.height);
						// The source is a blob: URL, so it is same-origin and the canvas is clean.
						poster = canvas.toDataURL('image/jpeg', POSTER_QUALITY);
					}
				} catch (e) {
					poster = null;
				}

				finish(duration, poster);
			}, {once: true});

			el.currentTime = target;
		};

		src = URL.createObjectURL(file);
		el.src = src;
	} catch (e) {
		finish(null, null);
	}
});

// Chrome and Firefox give WebM, Safari gives MP4. Both base types are already in the server's
// allowedVideoTypes; an empty result lets the browser pick whatever it likes.
const RECORD_TYPES = [
	'video/webm;codecs=vp9,opus',
	'video/webm;codecs=vp8,opus',
	'video/webm',
	'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
	'video/mp4',
];

const pickRecordType = () => {
	if (!window.MediaRecorder || !window.MediaRecorder.isTypeSupported) return '';
	for (const type of RECORD_TYPES) {
		if (window.MediaRecorder.isTypeSupported(type)) return type;
	}
	return '';
};

// Feature-detected rather than assumed. getUserMedia also needs a secure context, so this is
// false over plain HTTP to a LAN address even in a browser that supports everything.
const canRecord = () => !!(
	navigator.mediaDevices &&
	navigator.mediaDevices.getUserMedia &&
	window.MediaRecorder
);

const clock = (secs) => {
	const m = Math.floor(secs / 60);
	const s = Math.floor(secs % 60);
	return m + ':' + (s < 10 ? '0' : '') + s;
};

function reelPage(root) {
	const maxMB = parseInt(root.getAttribute('data-max-mb'), 10) || 100;
	const maxRecordSeconds = parseInt(root.getAttribute('data-max-record-seconds'), 10) || 300;
	const posterWidth = parseInt(root.getAttribute('data-poster-width'), 10) || 640;

	const fileInput = $('#vol-file-input');
	const progressWrap = $('#vol-upload-progress');
	const progressBar = $('#vol-upload-bar');
	const progressLabel = $('#vol-upload-label');

	let pendingOwner = null;

	// The one path into the reel, shared by a file the participant picked and a blob they
	// just recorded — so both get a poster and a duration on identical terms.
	const startUpload = async (ownerId, file, label) => {
		// Check the size before uploading a single byte — the server enforces this too.
		if (file.size > maxMB * 1024 * 1024) {
			window.alert(
				'That video is ' + (file.size / 1048576).toFixed(0) + 'MB. ' +
				'Videos must be ' + maxMB + 'MB or smaller.'
			);
			return;
		}

		progressWrap.style.display = '';
		progressBar.style.width = '0%';
		progressLabel.textContent = 'Reading ' + file.name + '…';

		const captured = await captureVideoPoster(file, posterWidth);

		progressLabel.textContent = 'Uploading ' + file.name + '…';

		let videoId = null;

		try {
			await upload(
				'/volunteer/reel/upload',
				file.name,
				file.type,
				file,
				{owner: ownerId, size: file.size, label: label || ''},
				(pct) => {
					const value = Math.round(pct * 100);
					progressBar.style.width = value + '%';
					progressLabel.textContent = 'Uploading ' + file.name + '… ' + value + '%';
				},
				(startData) => { videoId = startData && startData.videoId; }
			);
		} catch (err) {
			progressWrap.style.display = 'none';
			window.alert(errText(err) || 'That upload did not finish. Please try again.');
			return;
		}

		// The duration and the poster are things only the browser ever knew. Attach them to
		// the row /start minted for us — but never let a failure here report the upload as
		// broken, because by this point the video is safely in the bucket.
		if (videoId && (captured.duration || captured.poster)) {
			progressLabel.textContent = 'Finishing up…';
			try {
				await axios.post('/volunteer/reel/' + videoId + '/meta', {
					durationSeconds: captured.duration,
					poster: captured.poster,
				});
			} catch (e) {
				console.log('Could not attach audition metadata', e);
			}
		}

		window.location.reload();
	};

	$$('.vol-upload-btn').forEach((btn) => {
		btn.addEventListener('click', () => {
			pendingOwner = btn.getAttribute('data-owner');
			fileInput.value = '';
			fileInput.click();
		});
	});

	fileInput.addEventListener('change', async () => {
		const file = fileInput.files && fileInput.files[0];
		if (!file || !pendingOwner) return;

		await startUpload(pendingOwner, file, '');
	});

	// ── record in the browser ──────────────────────────────────────────────────
	//
	// Uploading assumes the participant already has a video file. Recording here removes the
	// find-an-app / export / upload detour that loses people before they ever apply.
	const initRecorder = () => {
		const modal = $('#vol-record-modal');

		// No camera support: drop the buttons entirely rather than leave them to fail on click.
		if (!modal || !canRecord()) {
			$$('.vol-record-btn').forEach((btn) => btn.parentNode.removeChild(btn));
			return;
		}

		const stage = $('#vol-record-stage', modal);
		const preview = $('#vol-record-preview', modal);
		const playback = $('#vol-record-playback', modal);
		const timerEl = $('#vol-record-timer', modal);
		const statusEl = $('#vol-record-status', modal);
		const labelInput = $('#vol-record-label', modal);

		let stream = null;
		let recorder = null;
		let chunks = [];
		let tick = null;
		let recorded = null;
		let owner = null;
		// Permission prompts and recorder stops both resolve asynchronously, and the
		// participant can close the dialog while either is in flight. Anything that lands
		// after that has to drop what it produced instead of leaving the camera live.
		let isOpen = false;

		const setState = (state) => stage.setAttribute('data-record-state', state);
		const stopTick = () => { if (tick) clearInterval(tick); tick = null; };

		// The browser keeps its recording indicator lit and the camera warm until every track
		// is stopped, so this has to run on every exit from the dialog.
		const releaseStream = () => {
			if (stream) stream.getTracks().forEach((track) => track.stop());
			stream = null;
			preview.srcObject = null;
		};

		const clearRecording = () => {
			if (recorded && recorded.url) URL.revokeObjectURL(recorded.url);
			recorded = null;
			playback.removeAttribute('src');
		};

		const reset = () => {
			isOpen = false;
			stopTick();
			if (recorder && recorder.state !== 'inactive') {
				try {
					recorder.stop();
				} catch (e) {
					// Already stopping; nothing to undo.
				}
			}
			recorder = null;
			chunks = [];
			releaseStream();
			clearRecording();
			timerEl.textContent = '';
			statusEl.textContent = '';
			labelInput.value = '';
			setState('idle');
		};

		const openCamera = async () => {
			statusEl.textContent = 'Waiting for camera permission…';

			let granted;
			try {
				granted = await navigator.mediaDevices.getUserMedia({
					video: {width: {ideal: 1280}, height: {ideal: 720}, facingMode: 'user'},
					audio: true,
				});
			} catch (e) {
				// Declining the camera is a normal choice, so say what to do instead.
				statusEl.textContent =
					'No camera access. Allow it in your browser settings, or upload a file instead.';
				return false;
			}

			// Closed while the permission prompt was up: take the camera straight back down.
			if (!isOpen) {
				granted.getTracks().forEach((track) => track.stop());
				return false;
			}

			stream = granted;
			statusEl.textContent = '';
			preview.srcObject = stream;
			preview.play().catch(() => {});
			return true;
		};

		const stopRecording = () => {
			stopTick();
			if (recorder && recorder.state !== 'inactive') recorder.stop();
		};

		const beginRecording = () => {
			const mimeType = pickRecordType();
			chunks = [];

			let active;
			try {
				active = mimeType
					? new window.MediaRecorder(stream, {mimeType: mimeType})
					: new window.MediaRecorder(stream);
			} catch (e) {
				statusEl.textContent = 'This browser could not start a recording. Upload a file instead.';
				return;
			}
			recorder = active;

			active.ondataavailable = (e) => {
				if (e.data && e.data.size) chunks.push(e.data);
			};

			// Held in a local rather than read off `recorder`, because reset() nulls that field
			// before this fires when the dialog is closed mid-recording.
			active.onstop = () => {
				stopTick();

				const type = active.mimeType || mimeType || 'video/webm';
				const blob = new Blob(chunks, {type: type});
				chunks = [];

				// Closed mid-take: the recording is discarded rather than resurrecting a
				// dialog the participant has already dismissed.
				if (!isOpen) return;

				// The camera stays open on purpose — "Record again" is one click away and
				// re-prompting for permission each time is worse than an idle stream.
				recorded = {blob: blob, type: type, url: URL.createObjectURL(blob)};
				playback.src = recorded.url;
				setState('review');
			};

			active.start();
			setState('recording');

			// Wall-clock rather than a tick count, so the cap holds even if timers are throttled.
			const startedAt = Date.now();
			timerEl.textContent = clock(0) + ' / ' + clock(maxRecordSeconds);

			tick = setInterval(() => {
				const elapsed = Math.floor((Date.now() - startedAt) / 1000);
				timerEl.textContent = clock(elapsed) + ' / ' + clock(maxRecordSeconds);
				if (elapsed >= maxRecordSeconds) stopRecording();
			}, 250);
		};

		$$('.vol-record-btn').forEach((btn) => {
			btn.addEventListener('click', async () => {
				owner = btn.getAttribute('data-owner');
				reset();
				isOpen = true;
				window.jQuery(modal).modal('show');
				await openCamera();
			});
		});

		$('#vol-record-start', modal).addEventListener('click', () => {
			if (stream) beginRecording();
		});

		$('#vol-record-stop', modal).addEventListener('click', stopRecording);

		$('#vol-record-retry', modal).addEventListener('click', async () => {
			clearRecording();
			timerEl.textContent = '';
			setState('idle');
			if (!stream) await openCamera();
		});

		$('#vol-record-use', modal).addEventListener('click', async () => {
			if (!recorded || !owner) return;

			const ext = String(recorded.type).indexOf('mp4') !== -1 ? 'mp4' : 'webm';
			const name = 'audition-' + Date.now() + '.' + ext;
			const label = labelInput.value.trim();
			const blob = recorded.blob;
			const ownerId = owner;

			// Hand the recording to the same path an uploaded file takes.
			let file;
			try {
				file = new File([blob], name, {type: recorded.type});
			} catch (e) {
				// Older Safari has no File constructor; the uploader only needs size and slice.
				blob.name = name;
				file = blob;
			}

			window.jQuery(modal).modal('hide');
			await startUpload(ownerId, file, label);
		});

		// Bound once against static markup. Binding per render is what made a single drop on
		// the board fire N moves — see §12 of the feature notes.
		window.jQuery(modal).on('hidden.bs.modal', reset);
	};

	initRecorder();

	$$('.vol-delete-video').forEach((btn) => {
		btn.addEventListener('click', async () => {
			const id = btn.getAttribute('data-video-id');
			const label = btn.getAttribute('data-label');

			// Warn about the real consequence before deleting, naming each affected audition.
			let message = 'Delete "' + label + '"?';
			try {
				const impact = await axios.get('/volunteer/reel/' + id + '/impact');
				if (impact.data.count) {
					message = 'Deleting "' + label + '" will cancel ' +
						impact.data.auditions.map((a) => a.description).join(', and ') + '.\n\nDelete it anyway?';
				}
			} catch (e) {
				// If the impact check fails, still confirm — just without the detail.
			}

			if (!window.confirm(message)) return;

			const restore = disable(btn, 'Deleting&hellip;');
			try {
				await axios.delete('/volunteer/reel/' + id);
				window.location.reload();
			} catch (err) {
				restore();
				window.alert(errText(err));
			}
		});
	});
}

// ─── boot ────────────────────────────────────────────────────────────────────

const PAGES = {
	index: indexPage,
	showing: showingPage,
	me: mePage,
	reel: reelPage,
};

const boot = () => {
	const root = $('[data-vol-public]');
	if (!root) return;

	const page = PAGES[root.getAttribute('data-vol-public')];
	if (page) page(root);
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
